/**
 * usePendingAppointments Hook
 * Custom hook for managing pending appointments list with search, pagination, and approval actions
 *
 * @version 2025.1.0
 */

import { useState, useEffect, useCallback } from 'react';
import { PendingAppointmentResponse } from '../types/appointment';
import appointmentService from '../services/appointmentService';
import { useSnackbar } from 'notistack';

interface UsePendingAppointmentsResult {
  appointments: PendingAppointmentResponse[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  search: string;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setSearch: (search: string) => void;
  refresh: () => void;
  approveAppointment: (id: number, notes?: string) => Promise<boolean>;
  rejectAppointment: (id: number, reason: string) => Promise<boolean>;
}

export const usePendingAppointments = (): UsePendingAppointmentsResult => {
  const { enqueueSnackbar } = useSnackbar();

  const [appointments, setAppointments] = useState<PendingAppointmentResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearchState] = useState('');

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });

  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await appointmentService.getPendingAll({
        page,
        limit,
        search: search || undefined,
        sortBy: 'selectedDate',
        sortOrder: 'ASC',
      });

      setAppointments(response.appointments);
      setPagination({
        total: response.total,
        page: response.page,
        limit: response.limit,
        totalPages: response.totalPages,
      });
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Erro ao carregar agendamentos pendentes';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, enqueueSnackbar]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleSetPage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSetLimit = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleSetSearch = useCallback((newSearch: string) => {
    setSearchState(newSearch);
    setPage(1);
  }, []);

  const refresh = useCallback(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const approveAppointment = useCallback(async (id: number, notes?: string): Promise<boolean> => {
    try {
      const result = await appointmentService.approveAppointment(id, { notes });
      enqueueSnackbar(result.message, { variant: 'success' });
      refresh();
      return true;
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Erro ao aprovar agendamento';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return false;
    }
  }, [enqueueSnackbar, refresh]);

  const rejectAppointment = useCallback(async (id: number, reason: string): Promise<boolean> => {
    try {
      const result = await appointmentService.rejectAppointment(id, { reason });
      enqueueSnackbar(result.message, { variant: 'success' });
      refresh();
      return true;
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Erro ao rejeitar agendamento';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return false;
    }
  }, [enqueueSnackbar, refresh]);

  return {
    appointments,
    loading,
    error,
    pagination,
    search,
    setPage: handleSetPage,
    setLimit: handleSetLimit,
    setSearch: handleSetSearch,
    refresh,
    approveAppointment,
    rejectAppointment,
  };
};

export default usePendingAppointments;
